import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';

import { Filter, Lock as LockIcon, RefreshCw, Unlock } from 'lucide-react';
import { toast } from 'react-toastify';

import { usePageHeaderContext } from '../context/PageHeaderContext';
import {
  IconButton,
  ListingLayout,
  LISTING_MIN_HEIGHT,
  Pagination,
  SearchBar,
  TableView,
} from './listing';

import type { AppFilterPayload, BaseListProps } from '../model';
import type { ColDef } from 'ag-grid-community';
import type { SortDirection } from './listing/TableView';

const BaseList = forwardRef(
  <T extends object>(props: BaseListProps<T>, ref: React.ForwardedRef<any>) => {
    const {
      title,
      breadcrumbs,
      searchBy,
      fetchData,
      transformData = (data) => data,
      columns,
      showChangeBtn = false,
      onChangeStatus,
      extractRowIds,
      enableCheckbox = false,
      onPayloadChange,
      storageKey,
    } = props;

    const { header, setTitle, setBreadcrumbs } = usePageHeaderContext();

    const defaultPageSizes = [10, 20, 50, 100];
    const resolvedStorageKey =
      storageKey ??
      `ui:${title.singular.toLowerCase().replace(/\s+/g, '-')}:pageSize`;
    const initialPageSize = (() => {
      if (typeof window === 'undefined') return 20;
      const stored = localStorage.getItem(resolvedStorageKey);
      if (!stored) return 20;
      const parsed = Number(stored);
      if (!Number.isFinite(parsed) || parsed <= 0) return 20;
      return defaultPageSizes.includes(parsed) ? parsed : 20;
    })();

    const [payload, setPayload] = useState<AppFilterPayload>({
      page: 1,
      limit: initialPageSize,
      sort: [],
      filters: [],
      keyword: '',
    });

    const [searchValue, setSearchValue] = useState('');
    const [sortState, setSortState] = useState<{
      field: string;
      direction: SortDirection;
    }>({ field: '', direction: null });

    const [selectedRowIds, setSelectedRowIds] = useState<Set<number>>(
      new Set()
    );
    const [showFilters, setShowFilters] = useState(false);

    const safePayload = {
      ...payload,
      page: payload.page || 1,
      limit: payload.limit || 10,
    };

    const [rowData, setRowData] = useState<T[]>([]);
    const [totalRows, setTotalRows] = useState<number>(0);

    const { data, isFetching, refetch } = fetchData(payload);

    useEffect(() => {
      if (typeof window !== 'undefined') {
        localStorage.setItem(resolvedStorageKey, JSON.stringify(payload.limit));
      }
    }, [payload.limit, resolvedStorageKey]);

    useEffect(() => {
      if (data) {
        const records = Array.isArray(data.data)
          ? data.data
          : (data.data as any)?.records || [];
        setRowData(transformData(records));
        const itemCount =
          data?.meta?.total ??
          (data?.data as any)?.pageMeta?.itemCount ??
          records.length ??
          0;
        setTotalRows(itemCount);
      }
    }, [data, transformData]);

    useEffect(() => {
      setSelectedRowIds(new Set());
    }, [rowData]);

    useImperativeHandle(ref, () => ({
      reload: refetch,
    }));

    const handleSearch = useCallback(
      (keyword: string) => {
        setPayload((prev) => {
          const newPayload = { ...prev, keyword, page: 1 };
          onPayloadChange?.(newPayload);
          return newPayload;
        });
      },
      [onPayloadChange]
    );

    const handleSortChange = useCallback(
      (next: { field: string; direction: SortDirection }) => {
        setSortState(next.direction ? next : { field: '', direction: null });
        setPayload((prev) => {
          const sort = next.direction
            ? [{ prop: next.field, dir: next.direction }]
            : [];
          const newPayload = { ...prev, sort };
          onPayloadChange?.(newPayload);
          return newPayload;
        });
      },
      [onPayloadChange]
    );

    const getRowId = useCallback(
      (row: T, index: number) => {
        const extracted = extractRowIds?.([row])?.[0];
        return extracted ?? (index as any);
      },
      [extractRowIds]
    );

    const isRowSelected = useCallback(
      (row: T, index: number) => selectedRowIds.has(getRowId(row, index)),
      [getRowId, selectedRowIds]
    );

    const toggleRow = useCallback(
      (row: T, index: number) => {
        const rowId = getRowId(row, index) as any;
        setSelectedRowIds((prev) => {
          const next = new Set(prev);
          if (next.has(rowId)) {
            next.delete(rowId);
          } else {
            next.add(rowId);
          }
          return next;
        });
      },
      [getRowId]
    );

    const allRowIds = useMemo(
      () => rowData.map((row, index) => getRowId(row, index) as any),
      [rowData, getRowId]
    );

    const isAllSelected =
      allRowIds.length > 0 &&
      allRowIds.every((id) => selectedRowIds.has(id as any));

    const toggleSelectAll = () => {
      if (isAllSelected) {
        setSelectedRowIds(new Set());
      } else {
        setSelectedRowIds(new Set(allRowIds));
      }
    };

    const selectionColumn: ColDef<T> = {
      headerName: '',
      field: '__select',
      width: 48,
      sortable: false,
      headerRenderer: () => (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={toggleSelectAll}
          aria-label="Select all rows"
          className="w-4 h-4 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
        />
      ),
      cellRenderer: ({ data }) => {
        if (!data) return null;
        const index = rowData.indexOf(data as T);
        return (
          <input
            type="checkbox"
            checked={isRowSelected(data as T, index)}
            onChange={() => toggleRow(data as T, index)}
            aria-label="Select row"
            className="w-4 h-4 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
          />
        );
      },
    };

    const finalColumns = useMemo(() => {
      if (!enableCheckbox) {
        return columns;
      }
      return [selectionColumn, ...columns];
    }, [columns, enableCheckbox, selectionColumn]);

    const onActivateDeactivate = useCallback(
      (status: string) => {
        if (!selectedRowIds.size) {
          toast.error(`Please select at least one ${title.singular}`);
          return;
        }
        onChangeStatus?.(Array.from(selectedRowIds) as any, status);
      },
      [onChangeStatus, selectedRowIds, title.singular]
    );

    const handleSearchSubmit = useCallback(
      (value: string) => {
        setSearchValue(value);
        handleSearch(value);
      },
      [handleSearch]
    );

    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [listHeight, setListHeight] = useState(0);

    useLayoutEffect(() => {
      if (!bodyRef.current) return;
      const updateHeight = () => {
        if (!bodyRef.current) return;
        setListHeight(bodyRef.current.clientHeight);
      };
      updateHeight();
      const observer = new ResizeObserver(updateHeight);
      observer.observe(bodyRef.current);
      window.addEventListener('resize', updateHeight);
      return () => {
        observer.disconnect();
        window.removeEventListener('resize', updateHeight);
      };
    }, []);

    const resolvedListHeight = listHeight || LISTING_MIN_HEIGHT;

    const breadcrumbsKey = useMemo(() => {
      if (!breadcrumbs || breadcrumbs.length === 0) return '';
      return breadcrumbs
        .map(
          (item) =>
            `${item.label}|${item.to ?? ''}|${item.href ?? ''}|${
              item.current ? '1' : '0'
            }`
        )
        .join('>');
    }, [breadcrumbs]);

    const resolvedBreadcrumbs = useMemo(() => breadcrumbs, [breadcrumbsKey]);

    useEffect(() => {
      if (title?.plural) {
        setTitle(title.plural);
      }
    }, [title, setTitle]);

    useEffect(() => {
      if (
        resolvedBreadcrumbs &&
        resolvedBreadcrumbs.length > 0 &&
        (header.breadcrumbs?.length ?? 0) <= 1
      ) {
        setBreadcrumbs(resolvedBreadcrumbs);
      }
    }, [resolvedBreadcrumbs, header.breadcrumbs?.length, setBreadcrumbs]);

    useEffect(() => {
      return () => {
        setTitle(undefined);
        setBreadcrumbs(undefined);
      };
    }, [setTitle, setBreadcrumbs]);

    const toolbarActions = useMemo(
      () => (
        <>
          <IconButton icon={RefreshCw} title="Refresh" onClick={refetch} />
          {showChangeBtn && (
            <>
              <IconButton
                icon={Unlock}
                title="Activate"
                onClick={() => onActivateDeactivate('Active')}
              />
              <IconButton
                icon={LockIcon}
                title="Inactivate"
                onClick={() => onActivateDeactivate('Inactive')}
              />
            </>
          )}
        </>
      ),
      [onActivateDeactivate, refetch, showChangeBtn, title.singular]
    );

    return (
      <ListingLayout
        footer={
          <Pagination
            currentPage={safePayload.page}
            itemsPerPage={safePayload.limit}
            totalItems={totalRows}
            totalPages={Math.max(1, Math.ceil(totalRows / safePayload.limit))}
            onPageChange={(newPage) =>
              setPayload((prev) => {
                const newPayload = { ...prev, page: newPage };
                onPayloadChange?.(newPayload);
                return newPayload;
              })
            }
            onItemsPerPageChange={(newLimit) =>
              setPayload((prev) => {
                const newPayload = { ...prev, limit: newLimit, page: 1 };
                onPayloadChange?.(newPayload);
                return newPayload;
              })
            }
          />
        }
      >
        <div className="bg-white dark:bg-neutral-950 border-b border-neutral-200 dark:border-neutral-800 p-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 max-w-sm">
              <SearchBar
                value={searchValue}
                onChange={setSearchValue}
                onSearch={handleSearchSubmit}
                placeholder={`Search by ${searchBy}`}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <IconButton
                icon={Filter}
                title="Filter"
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <span>{showFilters ? 'Hide Filter' : 'Filter'}</span>
              </IconButton>
              {(showChangeBtn || searchBy) && (
                <div className="w-px h-6 bg-neutral-200 dark:bg-neutral-800 mx-1 hidden sm:block" />
              )}
              {toolbarActions}
            </div>
          </div>

          {showFilters && (
            <div className="pt-4 border-t border-neutral-100 dark:border-neutral-900">
              <div className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                Filter Options
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Placeholders for actual filters if needed later */}
              </div>
            </div>
          )}
        </div>

        <div ref={bodyRef} className="flex-1 min-h-0">
          <TableView
            rows={rowData}
            columns={finalColumns}
            rowKey={getRowId}
            height={resolvedListHeight}
            minHeight={resolvedListHeight}
            disableInternalScroll
            sortState={
              sortState.field
                ? { field: sortState.field, direction: sortState.direction }
                : undefined
            }
            onSortChange={handleSortChange}
            isLoading={isFetching}
            emptyMessage="No matching records found"
          />
        </div>
      </ListingLayout>
    );
  }
);

BaseList.displayName = 'BaseList';

export default BaseList;
